import {
  Button,
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
} from "@design-system/shadcn";
import { pickDataAttributes, type DataAttributes } from "@design-system/utils";
import { Ellipsis } from "lucide-react";
import React, { type ComponentProps } from "react";

/** One entry of a row's action menu. */
interface DataTableRowAction {
  label: React.ReactNode;
  icon?: React.ReactNode;
  onSelect: () => void;
  disabled?: boolean;
  dataAttributes?: DataAttributes;
}

interface DataTableRowActionsProps extends Omit<
  ComponentProps<typeof Button>,
  "children" | "aria-label" | "variant" | "size"
> {
  actions: DataTableRowAction[];
  /**
   * Accessible name for the icon-only trigger. Required, and supplied by the
   * consumer: this package has no i18n runtime, and a per-row menu needs a name
   * that says WHICH row it belongs to (e.g. `Actions for Alma Bech`) rather than a
   * generic one repeated down the column.
   */
  triggerLabel: string;
  /** Test hook for the trigger; defaults to the shared row-actions hook. */
  "data-testid"?: string;
}

/**
 * A generic per-row action menu (design D6): an icon-only ellipsis trigger opening
 * a dropdown of consumer-supplied actions. Layout for the cell stays in the column
 * def; this renders only the trigger and its menu.
 */
function DataTableRowActions({
  actions,
  triggerLabel,
  "data-testid": dataTestId = "row-actions-trigger",
  ...props
}: DataTableRowActionsProps) {
  if (actions.length === 0) return null;

  return (
    // `modal={false}`: a modal Radix menu takes the body scroll lock, so opening
    // a row menu would collapse the page scrollbar and shift the table.
    <DropdownMenu modal={false}>
      <DropdownMenuTrigger asChild>
        <Button
          {...props}
          variant="ghost"
          size="smaller-icon"
          aria-label={triggerLabel}
          data-testid={dataTestId}
        >
          <Ellipsis aria-hidden />
        </Button>
      </DropdownMenuTrigger>
      <DropdownMenuContent align="end">
        {actions.map((action, index) => (
          <DropdownMenuItem
            // Labels may be JSX (a <Trans>), so they cannot key the list.
            key={index}
            {...pickDataAttributes(action.dataAttributes ?? {})}
            disabled={action.disabled}
            onSelect={action.onSelect}
          >
            {action.icon}
            {action.label}
          </DropdownMenuItem>
        ))}
      </DropdownMenuContent>
    </DropdownMenu>
  );
}

export { DataTableRowActions };
export type { DataTableRowAction, DataTableRowActionsProps };
